import { useId, useState, type FormEvent } from 'react'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { PhoneNumberInput } from '@/components/phone-number-input/PhoneNumberInput'
import { kDefaultLogoPath } from '@/constants/constants-ui'

type PostSignupPhoneStepValues = {
  phone: string
  textMessageConsent: boolean
}

type SharedPostSignupPhoneStepProps = {
  onSignOut: () => void
}

type ReadyPostSignupPhoneStepProps = {
  state: 'ready'
  onComplete: (values: PostSignupPhoneStepValues) => Promise<'completed' | 'invalid-phone'>
  onRetry?: never
}

type StartErrorPostSignupPhoneStepProps = {
  state: 'start-error'
  onComplete?: never
  onRetry: () => void
}

type PostSignupPhoneStepProps = SharedPostSignupPhoneStepProps & (ReadyPostSignupPhoneStepProps | StartErrorPostSignupPhoneStepProps)

export function PostSignupPhoneStep(props: PostSignupPhoneStepProps) {
  const consentId = useId()
  const [phone, setPhone] = useState('')
  const [textMessageConsent, setTextMessageConsent] = useState(false)
  const [phoneError, setPhoneError] = useState<string>()
  const [submitError, setSubmitError] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setPhoneError(undefined)
    setSubmitError(false)

    if (!phone) {
      setPhoneError('Enter a valid phone number.')
      return
    }

    if (props.state !== 'ready') return

    setSubmitting(true)
    try {
      const result = await props.onComplete({ phone, textMessageConsent })
      if (result === 'invalid-phone') {
        setPhoneError('Enter a valid phone number.')
      }
    } catch {
      setSubmitError(true)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <main className="bg-brand-neutral-gradient flex min-h-screen flex-1 flex-col items-center justify-center px-4 py-5 sm:px-6">
      <img className="mx-auto mb-4 w-[200px]" src={kDefaultLogoPath} alt="Paxton" />
      <section
        aria-labelledby="post-signup-phone-title"
        aria-describedby="post-signup-phone-subtitle"
        className="w-full max-w-lg rounded-lg bg-brand-neutral-50 px-4 pb-4 pt-5 text-left shadow-xl sm:p-8"
      >
        <div className="mb-4 text-center">
          <h1 id="post-signup-phone-title" className="text-xl font-semibold leading-6 text-brand-neutral-900">
            Add your mobile number
          </h1>
          <p id="post-signup-phone-subtitle" className="mt-2 text-sm text-brand-neutral-600">
            Required to secure your account and verify your identity.
          </p>
        </div>

        {props.state === 'start-error' ? (
          <div className="flex flex-col gap-2 pt-1">
            <InlineMessage variant="error">We couldn't continue. Please try again.</InlineMessage>
            <Button type="button" fullWidth onClick={props.onRetry}>
              Retry
            </Button>
          </div>
        ) : (
          <form className="flex flex-col gap-8 pt-1" onSubmit={handleSubmit}>
            <PhoneNumberInput
              value={phone}
              onChange={setPhone}
              defaultCountry="US"
              disabled={submitting}
              errorMessage={phoneError}
              label={null}
              placeholder="(555) 123-4567"
              required
            />

            <label htmlFor={consentId} className="flex cursor-pointer items-start gap-3 text-sm leading-5 text-brand-neutral-700">
              <input
                id={consentId}
                type="checkbox"
                checked={textMessageConsent}
                disabled={submitting}
                onChange={(event) => setTextMessageConsent(event.target.checked)}
                className="mt-0.5 size-5 shrink-0 cursor-pointer rounded border-brand-neutral-400 text-brand-500 focus:ring-brand-400 disabled:cursor-not-allowed"
              />
              <span>Paxton may text me at this number. Reply STOP to opt out.</span>
            </label>

            <div className="flex flex-col gap-2">
              {submitError && <InlineMessage variant="error">We couldn't save your mobile number. Please try again.</InlineMessage>}

              <Button type="submit" fullWidth loading={submitting}>
                Continue
              </Button>
            </div>
          </form>
        )}
      </section>
      <div className="mt-5 flex w-full max-w-lg">
        <Button type="button" variant="plain" color="primary" size="sm" onClick={props.onSignOut}>
          Sign out
        </Button>
      </div>
    </main>
  )
}
